import { Provider } from '@angular/core';
import { ApolloLink, HttpLink, InMemoryCache, NextLink, Observable, Operation } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { AuthService } from '@thx/auth';
import { AppsyncAuthTypes } from '@thx/enums';
import { APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLinkHandler } from 'apollo-angular/http';
import { ExecutionResult } from 'graphql';
import { lastValueFrom } from 'rxjs';
import { IGraphqlMockResponse } from './graphql-mock-response.interface';
import { IGraphqlProvidersConfig } from './graphql-providers-config.interface';

export class GraphqlProvidersService {
  public static readonly GRAPHQL_MOCK_INTERCEPTOR: string = 'GRAPHQL_MOCK';

  private _authService?: AuthService = new AuthService();
  private readonly _authType: AppsyncAuthTypes;
  private readonly _serverUrl: string;
  private readonly _token: string | undefined;
  private readonly _mocksEnabled: boolean;
  private _mocks: Map<string, IGraphqlMockResponse>;
  private readonly _production: boolean;
  private readonly _useRemote: boolean;

  constructor(config: IGraphqlProvidersConfig) {
    this._serverUrl = config.serverUrl;
    this._token = config.token;
    this._mocksEnabled = config.mocksEnabled;
    this._mocks = config.mocks;
    this._production = config.production;
    this._useRemote = config.useRemote;
    this._authType = config.authType || AppsyncAuthTypes.AMAZON_COGNITO_USER_POOLS;
  }

  public getProviders(): Provider[] {
    return [
      {
        provide: APOLLO_OPTIONS,
        useFactory: this._apolloMockProviders.bind(this),
        deps: [GraphqlProvidersService.GRAPHQL_MOCK_INTERCEPTOR],
      },
      {
        provide: GraphqlProvidersService.GRAPHQL_MOCK_INTERCEPTOR,
        useFactory: () =>
          new ApolloLink((operation: Operation, forward: any) => this._handleOperation(operation, forward)),
      },
    ];
  }

  private async _setHeaders(): Promise<{ [key: string]: string }> {
    if (this._token && this._authType === AppsyncAuthTypes.API_KEY) {
      return { ['x-api-key']: this._token };
    }
    if (this._authService) {
      const userSession = await lastValueFrom(this._authService.currentUserSession());
      if (userSession && userSession.tokens?.accessToken) {
        const accessToken = userSession.tokens.accessToken.toString();
        return { Authorization: accessToken };
      }
    }
    return {};
  }

  private _handleOperation(operation: Operation, forward: NextLink): any {
    let mock: IGraphqlMockResponse | undefined = this._mocks.get(operation.operationName);
    if (mock && mock.useMock && this._mocksEnabled) {
      // @ts-ignore TODO: figure out best way to mock ExecutionResult
      return Observable.of(mock.payload as ExecutionResult);
    }
    return forward(operation);
  }

  private _apolloLinks(mockForwarder: ApolloLink): ApolloLink {
    const headersLink = setContext(async (_, { headers }) => {
      const authHeaders = await this._setHeaders();
      return { headers: { ...headers, ...authHeaders } };
    });

    const httpLink = new HttpLink({ uri: this._serverUrl });
    return ApolloLink.from([headersLink, mockForwarder, httpLink].filter((link) => link !== null));
  }

  private _apolloMockProviders(mockProvider: ApolloLink): { cache: InMemoryCache; link: ApolloLink | HttpLinkHandler } {
    return {
      cache: new InMemoryCache({ addTypename: false }),
      link: this._apolloLinks(mockProvider),
    };
  }
}
